// Sensor finding + its helper cluster, extracted from app.js.
//
// Many helpers cross-call each other and several read app.js globals through
// currentEngineDescriptors / sensorEvidenceFlights. To avoid threading the
// same context through 20+ functions, this module uses a factory:
//
//   const helpers = createSensorSummaryHelpers({ currentEngineDescriptors, sensorEvidenceFlights });
//   // helpers is an object with all the bound helpers — sensorFinding, sensorSummaryDisplayLabel, etc.
//
// app.js destructures the returned object once at the top of the file. The
// hoisted function declarations for currentEngineDescriptors and
// sensorEvidenceFlights make that early call safe.

import { rawMetricDescriptor } from "../core/raw-metadata.js";
import {
  ENGINE_LABEL_STYLES,
  engineScopedChannelLabel,
  engineScopedProbeLabel,
} from "../core/engines.js";
import { cylinderNumberFromKey } from "../core/schema.js";
import { panelNumber } from "../core/display.js";
import { formatCompactElapsedTime, formatElapsedTime } from "../core/format.js";
import { sensorChannelType } from "../analysis/sensors.js";
import { instrumentationSuspicionFinding } from "./metric-findings.js";

export function createSensorSummaryHelpers({
  currentEngineDescriptors,
  sensorEvidenceFlights,
}) {
  function sensorSummaryPeerMismatchCount(summary) {
    return (summary?.coldMismatchCount ?? 0) + (summary?.hotMismatchCount ?? 0);
  }

  function sensorSummaryMarkedWindows(summary) {
    const windows = [...(summary?.conditionWindows ?? [])];
    if (summary?.flatline) {
      windows.push({
        ...summary.flatline,
        kind: "flatline",
        reason: "flatline",
      });
    }
    return windows
      .filter((window) => Number.isFinite(window.startTime))
      .sort((a, b) => (b.duration ?? 0) - (a.duration ?? 0));
  }

  function engineNarrativeProbeLabel(keyOrLabel) {
    const text = String(keyOrLabel ?? "").trim();
    if (!text || !(/^(egt|cht)\d{1,2}$/i.test(text) || /\b(EGT|CHT)\s*#?\s*\d{1,2}\b/i.test(text))) return "";
    return engineScopedProbeLabel(keyOrLabel, currentEngineDescriptors(), { style: ENGINE_LABEL_STYLES.NARRATIVE });
  }

  function sensorSummaryDisplayLabel(summary) {
    const descriptors = currentEngineDescriptors();
    const descriptorLabel = rawMetricDescriptor(summary?.key)[0];
    const keyOrLabel = summary?.key ?? summary?.label;
    const scopedLabel = engineScopedProbeLabel(keyOrLabel, descriptors);
    const channelLabel = summary?.key
      ? engineScopedChannelLabel(summary.key, descriptors, { style: ENGINE_LABEL_STYLES.NARRATIVE })
      : null;
    return engineNarrativeProbeLabel(summary?.key)
      || engineNarrativeProbeLabel(summary?.label)
      || (scopedLabel && scopedLabel !== keyOrLabel ? scopedLabel : "")
      || (channelLabel && channelLabel !== summary?.key ? channelLabel : "")
      || (descriptorLabel && descriptorLabel !== summary?.key ? descriptorLabel : "")
      || summary?.label
      || descriptorLabel;
  }

  function sensorValueText(summary, value) {
    const [, unit = "", digits = 0] = rawMetricDescriptor(summary?.key);
    return panelNumber(value, unit ? ` ${unit}` : "", digits);
  }

  function sensorSummaryOutOfRangeCue(summary, window = null) {
    const rangeMin = window?.rangeMin ?? summary?.rangeMin;
    const rangeMax = window?.rangeMax ?? summary?.rangeMax;
    const lowValue = window?.worstLowValue ?? summary?.worstLowValue;
    const highValue = window?.worstHighValue ?? summary?.worstHighValue;
    const lowOver = Number.isFinite(lowValue) && Number.isFinite(rangeMin) ? rangeMin - lowValue : null;
    const highOver = Number.isFinite(highValue) && Number.isFinite(rangeMax) ? highValue - rangeMax : null;
    const useHigh = Number.isFinite(highOver) && (!Number.isFinite(lowOver) || highOver >= lowOver);
    const useLow = !useHigh && Number.isFinite(lowOver);
    if (!useHigh && !useLow) return null;
    const label = sensorSummaryDisplayLabel(summary);
    const value = useHigh ? highValue : lowValue;
    const limit = useHigh ? rangeMax : rangeMin;
    const exceedAmount = useHigh ? highOver : lowOver;
    const time = useHigh
      ? (window?.worstHighTime ?? summary?.worstHighTime)
      : (window?.worstLowTime ?? summary?.worstLowTime);
    const direction = useHigh ? "above" : "below";
    const verb = useHigh ? "peaked at" : "fell to";
    const valueText = sensorValueText(summary, value);
    const limitText = sensorValueText(summary, limit);
    const overText = sensorValueText(summary, exceedAmount);
    return {
      label,
      value,
      limit,
      exceedAmount,
      time,
      direction,
      valueText,
      limitText,
      overText,
      short: `${valueText} vs ${limitText} limit`,
      detail: `${overText} ${direction} limit${Number.isFinite(time) ? ` at ${formatCompactElapsedTime(time)}` : ""}`,
      sentence: `${label} ${verb} ${valueText}, ${overText} ${direction} the ${limitText} sensor sanity limit.`,
    };
  }

  function sensorSummaryHasReviewCue(summary, topKey = "") {
    if (!summary) return false;
    const peerMismatchCount = sensorSummaryPeerMismatchCount(summary);
    const isFuelFlow = sensorChannelType(summary.key) === "fuelFlow";
    const fuelFlowHasHardCue = Boolean(
      summary.zeroCount > 0
        || summary.outOfRangeCount > 0
        || summary.flatline
        || summary.jumpWindow
        || (summary.conditionWindow && summary.conditionWindow.kind !== "jumps")
    );
    return summary.key === topKey
      || (summary.suspicion >= 18 && (!isFuelFlow || fuelFlowHasHardCue))
      || sensorSummaryMarkedWindows(summary).length > 0
      || summary.zeroCount > 0
      || summary.outOfRangeCount > 0
      || (summary.impossibleJumpCount > 1 && (!isFuelFlow || fuelFlowHasHardCue))
      || peerMismatchCount >= 30
      || summary.coldMismatchRatio >= 0.08
      || summary.hotMismatchRatio >= 0.08
      || summary.missingRatio >= 0.08;
  }

  function sensorReviewSummaries(metric, options: Record<string, any> = {}) {
    const excludeKeys = new Set(Array.isArray(options.excludeKeys) ? options.excludeKeys : [options.excludeKey].filter(Boolean));
    const topKey = metric?.details?.top?.key ?? "";
    const summaries = metric?.details?.summaries?.length
      ? metric.details.summaries
      : [metric?.details?.top].filter(Boolean);
    return summaries
      .filter((summary) => !excludeKeys.has(summary?.key))
      .filter((summary) => sensorSummaryHasReviewCue(summary, topKey))
      .sort((a, b) => {
        const aWindow = sensorSummaryMarkedWindows(a)[0];
        const bWindow = sensorSummaryMarkedWindows(b)[0];
        return (b.suspicion ?? 0) - (a.suspicion ?? 0)
          || (bWindow?.duration ?? 0) - (aWindow?.duration ?? 0)
          || String(a.label ?? a.key).localeCompare(String(b.label ?? b.key));
      });
  }

  function sensorSummaryIsPrimaryConcern(summary, topKey = "") {
    if (!summary) return false;
    const window = sensorSummaryMarkedWindows(summary)[0];
    const peerMismatchCount = sensorSummaryPeerMismatchCount(summary);
    const isFuelFlow = sensorChannelType(summary.key) === "fuelFlow";
    const fuelFlowHasHardCue = Boolean(
      summary.zeroCount > 0
        || summary.outOfRangeCount > 0
        || summary.flatline
        || summary.jumpWindow
        || (window && window.kind !== "jumps")
    );
    return summary.key === topKey
      || (summary.suspicion >= 45 && (!isFuelFlow || fuelFlowHasHardCue))
      || (window?.duration ?? 0) >= 600
      || summary.outOfRangeCount > 0
      || summary.zeroCount > 0
      || (summary.impossibleJumpCount >= 3 && (!isFuelFlow || fuelFlowHasHardCue))
      || peerMismatchCount >= 1000;
  }

  function sensorPrimarySummaries(metric, limit = 3) {
    const topKey = metric?.details?.top?.key ?? "";
    return sensorReviewSummaries(metric)
      .filter((summary) => sensorSummaryIsPrimaryConcern(summary, topKey))
      .slice(0, limit);
  }

  function sensorSummaryEvidenceParts(summary) {
    const parts = [];
    const window = sensorSummaryMarkedWindows(summary)[0] ?? null;
    const peerMismatchCount = sensorSummaryPeerMismatchCount(summary);
    const rangeCue = sensorSummaryOutOfRangeCue(summary, window);
    if (summary?.coolingRateProbeCue?.rateFpm) parts.push(`${Math.round(summary.coolingRateProbeCue.rateFpm)} F/min CHT step`);
    if (window) parts.push(`${window.reason ?? "signal review"} for ${formatElapsedTime(window.duration)}`);
    if (summary?.zeroCount) parts.push(`${summary.zeroCount.toLocaleString()} zero/dropout sample${summary.zeroCount === 1 ? "" : "s"}`);
    if (summary?.outOfRangeCount) parts.push(rangeCue ? `${rangeCue.short} (${rangeCue.detail})` : `${summary.outOfRangeCount.toLocaleString()} out-of-range sample${summary.outOfRangeCount === 1 ? "" : "s"}`);
    if (summary?.impossibleJumpCount) parts.push(`${summary.impossibleJumpCount.toLocaleString()} abrupt data jump${summary.impossibleJumpCount === 1 ? "" : "s"}`);
    if (peerMismatchCount) parts.push(`${peerMismatchCount.toLocaleString()} peer-mismatch sample${peerMismatchCount === 1 ? "" : "s"}`);
    if (!parts.length && summary?.missingRatio >= 0.08) parts.push(`${Math.round(summary.missingRatio * 100)}% missing`);
    return parts;
  }

  function sensorSummaryEvidenceText(summary, limit = 2) {
    const parts = sensorSummaryEvidenceParts(summary);
    return parts.length ? parts.slice(0, limit).join(" · ") : "Probe-quality pattern needs review";
  }

  function sensorSummaryWindowText(summary, limit = 3) {
    const windows = sensorSummaryMarkedWindows(summary).slice(0, limit);
    if (!windows.length) return "No marked window";
    return windows.map((window) => {
      const start = formatCompactElapsedTime(window.startTime);
      const end = Number.isFinite(window.endTime) ? formatCompactElapsedTime(window.endTime) : start;
      return `${window.reason ?? "signal review"} ${start}-${end}`;
    }).join("; ");
  }

  function sensorSummaryReportDetail(summary) {
    const label = sensorSummaryDisplayLabel(summary);
    const type = sensorChannelType(summary?.key).toUpperCase();
    const pairedType = type === "CHT" ? "EGT" : "CHT";
    const cylinder = cylinderNumberFromKey(summary?.key);
    const pairedChannel = Number.isFinite(cylinder)
      ? engineNarrativeProbeLabel(`${pairedType}${cylinder}`)
      : `same-cylinder ${pairedType}`;
    const peerType = type === "CHT" ? "CHT" : "EGT";
    return `${label} should be reviewed as a probe/data-quality channel first. Compare it with peer ${peerType} probes and ${pairedChannel} before leaning on dependent engine findings.`;
  }

  function sensorChannelList(labels) {
    const clean = labels.map((label) => String(label ?? "").trim()).filter(Boolean);
    if (!clean.length) return "probe channels";
    if (clean.length === 1) return clean[0];
    if (clean.length === 2) return `${clean[0]} and ${clean[1]}`;
    return `${clean.slice(0, -1).join(", ")}, and ${clean.at(-1)}`;
  }

  function sensorPairedChannelLabel(summary) {
    const type = sensorChannelType(summary?.key).toUpperCase();
    const cylinder = cylinderNumberFromKey(summary?.key);
    if (!Number.isFinite(cylinder)) return "";
    return engineNarrativeProbeLabel(`${type === "CHT" ? "EGT" : "CHT"}${cylinder}`);
  }

  function sensorPrimaryWindowSentence(summaries) {
    const windows = summaries
      .map((summary) => {
        const window = sensorSummaryMarkedWindows(summary)[0];
        if (!window) return "";
        const label = sensorSummaryDisplayLabel(summary);
        const rangeCue = sensorSummaryOutOfRangeCue(summary, window);
        const windowText = `from ${formatCompactElapsedTime(window.startTime)} to ${formatCompactElapsedTime(window.endTime)}`;
        if (window.kind === "range" && rangeCue) return `${rangeCue.sentence} It was flagged ${windowText}`;
        return `${label} is marked ${window.reason ?? "signal review"} ${windowText}`;
      })
      .filter(Boolean);
    if (!windows.length) return "";
    return `${windows.join("; ")}.`;
  }

  function sensorRelatedReviewSentence(metric, excludeKeys) {
    const related = sensorReviewSummaries(metric, { excludeKeys: [...new Set(Array.isArray(excludeKeys) ? excludeKeys : [excludeKeys].filter(Boolean))] }).slice(0, 3);
    if (!related.length) return "";
    return `Other probe channels also need review: ${related.map((summary) => `${sensorSummaryDisplayLabel(summary)} (${sensorSummaryEvidenceText(summary, 2)})`).join("; ")}.`;
  }

  function sensorRelatedEvidenceItems(metric, excludeKeys) {
    return sensorReviewSummaries(metric, { excludeKeys: [...new Set(Array.isArray(excludeKeys) ? excludeKeys : [excludeKeys].filter(Boolean))] }).slice(0, 3).map((summary) => ({
      label: `${sensorSummaryDisplayLabel(summary)} probe cue`,
      value: sensorSummaryWindowText(summary, 1),
      detail: sensorSummaryEvidenceText(summary, 2),
    }));
  }

  // Systemic high-frequency noise (>= 2 probes of a bank jittering together).
  // Kept as an inner helper (not exported) so createSensorSummaryHelpers keeps
  // its pinned key count. Advisory, plain-language: names the bank + worst probe,
  // gives possible causes and things to check — never a diagnosis.
  function sensorNoiseFinding(metric, top) {
    const bankType = sensorChannelType(top.key).toUpperCase();
    const affected = sensorReviewSummaries(metric)
      .filter((summary) => summary.conditionWindow?.kind === "noise" && summary.noiseSuspicion > 0);
    const labels = affected.map((summary) => sensorSummaryDisplayLabel(summary)).filter(Boolean);
    const count = top.noiseAffectedCount ?? labels.length ?? 1;
    const bankTotal = (metric.details?.summaries ?? [])
      .filter((summary) => sensorChannelType(summary?.key) === sensorChannelType(top.key)).length;
    const rms = Number.isFinite(top.noiseSystemicRms) ? top.noiseSystemicRms.toFixed(1) : null;
    // Name the actual worst probe, not `top` — with co-faults `top` may not be the
    // highest-RMS member, which would print "worst on X" with a value below the bank average.
    const worst = affected.reduce((worstSoFar, summary) => (
      (summary.noiseRms ?? 0) > (worstSoFar?.noiseRms ?? -Infinity) ? summary : worstSoFar
    ), affected[0] ?? top);
    const worstLabel = sensorSummaryDisplayLabel(worst) || `${bankType} probe`;
    const worstRms = Number.isFinite(worst.noiseRms) ? worst.noiseRms.toFixed(1) : null;
    const window = top.conditionWindow ?? null;
    const bankPhrase = bankTotal >= 2 && count >= bankTotal
      ? `all ${count} ${bankType} probes`
      : (labels.length ? sensorChannelList(labels) : `the ${bankType} probes`);
    const purpose = "This screen looks for probe channels whose data does not look trustworthy in this log. Beyond dropouts and impossible jumps, it also weighs how noisy each channel's signal is once the engine is running steady.";
    const observation = `${bankPhrase} read noisy in steady cruise — about ${rms ?? "an elevated amount"} °F of sample-to-sample jitter${worstRms ? ` (worst on ${worstLabel} at ${worstRms} °F)` : ""}.`;
    const commonMode = "Because the probes jitter together rather than one on its own, this usually points to a shared ground, probe-bus, or wiring/connector issue rather than real engine behavior.";
    const derivedCards = bankType === "CHT" ? "cooling and cylinder-health cards" : "EGT balance and valve-rhythm cards";
    const downstream = `Treat the ${derivedCards} as slightly lower-confidence until the probe wiring and grounds are checked.`;
    return {
      title: `Noisy signal on the ${bankType} probes`,
      metaItems: [
        rms ? `signal jitter ${rms} °F RMS in cruise` : "elevated signal jitter in cruise",
        `${count}${bankTotal >= count ? ` of ${bankTotal}` : ""} ${bankType} probe${count === 1 ? "" : "s"} affected`,
        "common-mode — probes jitter together",
      ],
      body: `${purpose}\n\n${[observation, commonMode, downstream].filter(Boolean).join(" ")}`,
      references: sensorEvidenceFlights(top.key),
      evidence: [{
        label: `${bankType} bank noise (steady cruise)`,
        value: `${rms ?? "elevated"} °F RMS · ${count} probe${count === 1 ? "" : "s"}`,
        detail: `worst ${worstLabel}${worstRms ? ` ${worstRms} °F` : ""}${window && Number.isFinite(window.startTime) ? ` · marked ${formatCompactElapsedTime(window.startTime)}-${formatCompactElapsedTime(window.endTime)}` : ""}`,
      }],
      causes: [
        [`Shared ${bankType} ground, probe-bus, or connector noise`, 0.42],
        ["Harness routed near an ignition or alternator noise source", 0.24],
        ["Probe or monitor-channel degradation", 0.2],
        ["Real engine behavior if corroborated by peers and fuel flow", 0.14],
      ],
      actions: [
        ["medium", `Review the raw ${bankType} traces in cruise for the jitter`],
        ["medium", `Check ${bankType} probe grounds, the shared probe reference/bus, and connector security`],
        ["low", "Compare against nearby flights to see whether the noise persists"],
        ["low", `Use ${bankType}-derived cards as advisory until the probe signal is trusted`],
      ],
      confidence: "Medium",
    };
  }

  function sensorFinding(metric) {
    if (Number.isFinite(metric?.details?.sharedReferenceCue?.score)
      && metric.details.sharedReferenceCue.score < 88) {
      return instrumentationSuspicionFinding({
        score: metric.details.sharedReferenceCue.score,
        details: {
          window: metric.details.sharedReferenceCue.window,
          powerChannels: metric.details.sharedReferenceCue.powerChannels,
          flaggedChannels: metric.details.sharedReferenceCue.flaggedChannels,
          flaggedEgtCount: metric.details.sharedReferenceCue.flaggedEgtCount,
          flaggedChtCount: metric.details.sharedReferenceCue.flaggedChtCount,
          flaggedTitCount: metric.details.sharedReferenceCue.flaggedTitCount,
          synchronyRatio: metric.details.sharedReferenceCue.synchronyRatio,
          longestOverlapSeconds: metric.details.sharedReferenceCue.longestOverlapSeconds,
        },
      });
    }
    const top = metric.details.top ?? {};
    if (top.noiseSuspicion > 0 && top.conditionWindow?.kind === "noise" && (top.noiseAffectedCount ?? 0) > 1) {
      return sensorNoiseFinding(metric, top);
    }
    const label = sensorSummaryDisplayLabel(top) || "Probe channel";
    const peerMismatchCount = (top.coldMismatchCount ?? 0) + (top.hotMismatchCount ?? 0);
    const condition = top.conditionWindow ?? top.conditionWindows?.[0] ?? null;
    const rangeCue = sensorSummaryOutOfRangeCue(top, condition);
    const symptoms = [];
    if (condition) symptoms.push(`${condition.reason ?? "signal review"} for ${formatElapsedTime(condition.duration)}`);
    if (top.flatline) symptoms.push(`flat for ${formatElapsedTime(top.flatline.duration)}`);
    if (top.zeroCount) symptoms.push(`${top.zeroCount.toLocaleString()} zero/dropout sample${top.zeroCount === 1 ? "" : "s"}`);
    if (top.outOfRangeCount) symptoms.push(rangeCue ? `${rangeCue.short} (${rangeCue.detail})` : `${top.outOfRangeCount.toLocaleString()} out-of-range sample${top.outOfRangeCount === 1 ? "" : "s"}`);
    if (top.impossibleJumpCount) symptoms.push(`${top.impossibleJumpCount.toLocaleString()} abrupt data jump${top.impossibleJumpCount === 1 ? "" : "s"}`);
    if (peerMismatchCount) symptoms.push(`${peerMismatchCount.toLocaleString()} peer-mismatch sample${peerMismatchCount === 1 ? "" : "s"}`);
    if (!symptoms.length && top.missingRatio >= 0.08) symptoms.push(`${Math.round(top.missingRatio * 100)}% missing`);
    const primarySummaries = sensorPrimarySummaries(metric);
    const primaryKeys = primarySummaries.map((summary) => summary.key);
    const primaryLabels = primarySummaries.map((summary) => (
      sensorSummaryDisplayLabel(summary)
    ));
    const primaryLabel = sensorChannelList(primaryLabels.length ? primaryLabels : [label]);
    const pairedLabels = primarySummaries
      .map(sensorPairedChannelLabel)
      .filter(Boolean);
    const primaryEvidence = primarySummaries.map((summary) => ({
      label: `${sensorSummaryDisplayLabel(summary)} probe cue`,
      value: sensorSummaryOutOfRangeCue(summary, sensorSummaryMarkedWindows(summary)[0])?.short ?? sensorSummaryWindowText(summary, 1),
      detail: sensorSummaryEvidenceText(summary, 3),
    }));
    const primaryWindowSentence = sensorPrimaryWindowSentence(primarySummaries);
    const metaItems = primarySummaries.length > 1
      ? primarySummaries.map((summary) => `${sensorSummaryDisplayLabel(summary)}: ${sensorSummaryEvidenceText(summary, 2)}`)
      : symptoms.length ? symptoms.slice(0, 3) : (metric.note ? [metric.note] : []);
    const relatedSentence = sensorRelatedReviewSentence(metric, primaryKeys.length ? primaryKeys : [top.key]);
    const multiplePrimary = primarySummaries.length > 1;
    const purpose = "This screen looks for probe channels whose data does not look trustworthy in this log — readings that drop out, jump impossibly far between samples, sit outside the expected range, or fail to track the other cylinders. When a probe is unreliable, the engine-health screens that depend on it become less reliable too.";
    const observationSentence = multiplePrimary
      ? `${primaryLabel} all show data-quality issues here.`
      : `${label} shows the strongest data-quality issue in this log.`;
    const windowSentence = primaryWindowSentence
      || (condition ? `The issue persists as a marked condition from ${formatCompactElapsedTime(condition.startTime)} to ${formatCompactElapsedTime(condition.endTime)}, so playback keeps it annunciated through that interval rather than treating it as a single event.` : "");
    const downstreamSentence = "Treat the engine-health cards (CHT, EGT balance, valve rhythm, cooling) as lower-confidence findings until these channels are checked against the raw trace and nearby flights.";
    return {
      title: `Suspect probe data on ${primaryLabel}`,
      metaItems,
      body: `${purpose}\n\n${[observationSentence, windowSentence, relatedSentence, downstreamSentence].filter(Boolean).join(" ")}`,
      references: sensorEvidenceFlights(top.key),
      evidence: [
        ...primaryEvidence,
        ...sensorRelatedEvidenceItems(metric, primaryKeys.length ? primaryKeys : [top.key]),
      ].filter(Boolean),
      causes: [
        [`${primaryLabel} probe, wiring, connector, or monitor channel issue`, 0.4],
        ["Temporary data-logging dropout or import normalization issue", 0.24],
        ["Probe placement, grounding, or response-time difference", 0.18],
        ["Real cylinder behavior if corroborated by adjacent channels", 0.18],
      ],
      actions: [
        ["medium", `Review the raw ${primaryLabel} traces around the highlighted samples`],
        ["medium", `Compare ${primaryLabel}${pairedLabels.length ? ` against paired channels (${sensorChannelList(pairedLabels)})` : ""} and nearby flights`],
        ["medium", "Check probe wiring, connector security, grounding, and monitor channel assignment if it repeats"],
        ["low", "Use the other engine-health cards as advisory until the probe channel is trusted"],
      ],
      confidence: (top.zeroCount || top.outOfRangeCount || top.impossibleJumpCount > 1 || top.flatline) ? "Medium" : "Low",
    };
  }

  return {
    engineNarrativeProbeLabel,
    sensorChannelList,
    sensorFinding,
    sensorPairedChannelLabel,
    sensorPrimarySummaries,
    sensorPrimaryWindowSentence,
    sensorRelatedEvidenceItems,
    sensorRelatedReviewSentence,
    sensorReviewSummaries,
    sensorSummaryDisplayLabel,
    sensorSummaryEvidenceParts,
    sensorSummaryEvidenceText,
    sensorSummaryHasReviewCue,
    sensorSummaryIsPrimaryConcern,
    sensorSummaryMarkedWindows,
    sensorSummaryOutOfRangeCue,
    sensorSummaryPeerMismatchCount,
    sensorSummaryReportDetail,
    sensorSummaryWindowText,
    sensorValueText,
  };
}
